import React, { useState } from 'react';
import { loginWithGoogleExtension } from '../auth/auth.extension';
import { Button } from '../components/Button';
import {
  LayoutGrid,
  Zap,
  TrendingUp,
  Shield,
  CheckCircle2
} from 'lucide-react';

declare const chrome: any;

export const WelcomeView: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    try {
      console.log('STEP 1: starting login');

      const googleToken = await loginWithGoogleExtension();
      console.log('STEP 2: googleToken', googleToken);

      const res = await fetch(
        'https://app.eisenhowerpro.com/api/auth/extension',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: googleToken }),
        }
      );

      console.log('STEP 3: response status', res.status);

      const text = await res.text();
      console.log('STEP 4: response body', text);

      if (!res.ok) throw new Error(text);

      const { sessionToken } = JSON.parse(text);
      chrome.storage.local.set({ sessionToken });

      chrome.tabs.create({ url: 'extension.html' });
    } catch (e) {
      console.error('LOGIN ERROR:', e);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
      <div className="relative max-w-6xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-gray-100">

        {/* BACKGROUND BLOBS */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-40"></div>

        {/* LEFT SIDE */}
        <div className="relative md:w-3/5 p-10 md:p-14 flex flex-col justify-center z-10">
          <div className="flex items-center gap-3 mb-8">
            <img
              src="logo.png"
              alt="Eisenhower Pro"
              className="w-12 h-12 rounded-xl shadow-md"
            />
            <h1 className="text-2xl font-bold text-gray-900">
              Eisenhower Pro
            </h1>
          </div>

          <h2 className="text-4xl font-extrabold text-gray-900 leading-tight mb-6">
            Master your time,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              not just your tasks.
            </span>
          </h2>

          <p className="text-lg text-gray-600 max-w-md mb-10">
            The productivity system that combines the
            <strong> Eisenhower Matrix</strong> with
            <strong> Deep Focus Gamification</strong>.
          </p>

          <div className="space-y-5">
            <Feature
              icon={<LayoutGrid size={20} />}
              title="Scientific Prioritization"
              text="Decide what truly matters before it becomes urgent."
            />
            <Feature
              icon={<Zap size={20} />}
              title="Deep Focus Mode"
              text="Block distractions and enter flow state."
            />
            <Feature
              icon={<TrendingUp size={20} />}
              title="Productivity XP"
              text="Level up as you execute meaningful work."
            />
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="relative md:w-2/5 bg-gray-50 p-10 md:p-14 flex flex-col justify-center border-l border-gray-100 z-10">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center border">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Get Started
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Sign in to sync your tasks across devices
            </p>

            <Button
              onClick={handleLogin}
              disabled={loading}
              className="w-full h-12 text-base shadow-lg shadow-blue-500/20"
            >
              {loading ? 'Connecting…' : 'Sign in with Google'}
            </Button>

            <p className="text-[11px] text-gray-400 mt-5">
              Secure authentication via Google
            </p>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="absolute bottom-6 flex gap-6 text-gray-400 text-sm">
        <span className="flex items-center gap-1">
          <Shield size={14} /> Secure & Private
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 size={14} /> Chrome Best Practices
        </span>
      </div>
    </div>
  );
};

const Feature = ({
  icon,
  title,
  text
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) => (
  <div className="flex items-start gap-4">
    <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
      {icon}
    </div>
    <div>
      <h4 className="font-semibold text-gray-800">{title}</h4>
      <p className="text-sm text-gray-600">{text}</p>
    </div>
  </div>
);
